import { useCallback, useEffect, useRef, useState, type FormEvent } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom"
import { ApiError, acceptSession, api, refreshSession, toApiMessage } from "../api/client"
import type { AuthSession, FamilySummary } from "../api/types"
import { useAuth } from "../auth/AuthContext"
import { Brand } from "../components/AppShell"
import { Icon } from "../components/Icon"
import { Button, Field, LoadingState, showToast } from "../components/ui"
import { usePublicConfig } from "../config/PublicConfigContext"
import { normalizeSecondFactor, rawSecondFactor } from "../lib/input"

function AuthFrame({ children, quote }: { children: React.ReactNode; quote: string }) {
  return (
    <main className="auth-layout">
      <section className="auth-visual">
        <div className="auth-visual__glow auth-visual__glow--one" />
        <div className="auth-visual__glow auth-visual__glow--two" />
        <div className="auth-visual__inner">
          <Brand />
          <div className="auth-visual__message">
            <span className="auth-visual__kicker">Своё важнее большого</span>
            <h1>{quote}</h1>
            <p>Договорённости, награды и семейные традиции — в одном понятном месте.</p>
          </div>
          <div className="auth-coins" aria-hidden="true">
            <span className="auth-coin auth-coin--one">☀️</span>
            <span className="auth-coin auth-coin--two">♥</span>
            <span className="auth-coin auth-coin--three">★</span>
          </div>
          <div className="auth-visual__note">
            <Icon name="shield" size={18} /> Данные остаются на вашем сервере
          </div>
        </div>
      </section>
      <section className="auth-panel">
        <div className="auth-panel__mobile-brand">
          <Brand />
        </div>
        <div className="auth-card">{children}</div>
      </section>
    </main>
  )
}

function secondFactorRequired(reason: unknown) {
  if (!(reason instanceof ApiError)) return false
  const details = reason.details
  const code =
    details && typeof details === "object" && "code" in details ? String((details as { code?: unknown }).code) : ""
  return code === "SECOND_FACTOR_REQUIRED" || /2fa|totp|second.factor|одноразов|код/i.test(reason.message)
}

export function LoginPage() {
  const { user, booting, login } = useAuth()
  const { registrationEnabled } = usePublicConfig()
  const navigate = useNavigate()
  const location = useLocation()
  const [values, setValues] = useState({ login: "", password: "", secondFactor: "" })
  const [step, setStep] = useState<"credentials" | "factor">("credentials")
  const [factorMode, setFactorMode] = useState<"totp" | "recovery">("totp")
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const lastAutoCode = useRef("")

  const authenticate = useCallback(
    async (secondFactor?: string) => {
      setError("")
      setSubmitting(true)
      try {
        const session = await login({
          login: values.login.trim(),
          password: values.password,
          deviceName: navigator.userAgent.includes("Mobile") ? "Телефон" : "Браузер",
          ...(secondFactor ? { secondFactor } : {})
        })
        const requested = (location.state as { from?: string } | null)?.from ?? "/"
        navigate(session.user.requiresTwoFactorSetup ? "/settings" : requested, { replace: true })
      } catch (reason) {
        if (secondFactorRequired(reason) && !secondFactor) {
          setStep("factor")
          setError("")
        } else {
          setError(toApiMessage(reason, "Не удалось войти"))
        }
      } finally {
        setSubmitting(false)
      }
    },
    [location.state, login, navigate, values.login, values.password]
  )

  useEffect(() => {
    if (step !== "factor" || factorMode !== "totp" || submitting) return
    const raw = rawSecondFactor(values.secondFactor)
    if (!/^\d{6}$/.test(raw)) {
      lastAutoCode.current = ""
      return
    }
    if (lastAutoCode.current === raw) return
    lastAutoCode.current = raw
    void authenticate(raw)
  }, [authenticate, factorMode, step, submitting, values.secondFactor])

  if (!booting && user) return <Navigate to="/" replace />

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void authenticate(step === "factor" ? rawSecondFactor(values.secondFactor) : undefined)
  }

  return (
    <AuthFrame quote="Маленькая экономика большой семьи.">
      <div className="auth-card__heading">
        <h2>{step === "credentials" ? "Войти" : "Подтвердите вход"}</h2>
        <p>
          {step === "credentials"
            ? "Введите данные своего аккаунта."
            : `Код для @${values.login} из приложения-аутентификатора.`}
        </p>
      </div>
      <form className="form-stack" onSubmit={submit}>
        {step === "credentials" ? (
          <>
            <Field label="Логин">
              <input
                autoComplete="username"
                required
                minLength={3}
                value={values.login}
                onChange={(event) => setValues({ ...values, login: event.target.value.trimStart() })}
                placeholder="например, nikita"
                autoFocus
              />
            </Field>
            <Field label="Пароль">
              <input
                type="password"
                autoComplete="current-password"
                required
                minLength={10}
                value={values.password}
                onChange={(event) => setValues({ ...values, password: event.target.value })}
                placeholder="Ваш пароль"
              />
            </Field>
          </>
        ) : (
          <>
            <Field
              label={factorMode === "totp" ? "Код 2FA" : "Recovery-код"}
              hint={
                factorMode === "totp"
                  ? "Отправим автоматически после ввода шести цифр."
                  : "Введите один из сохранённых одноразовых кодов."
              }
            >
              <input
                className={factorMode === "totp" ? "otp-input" : ""}
                inputMode={factorMode === "totp" ? "numeric" : "text"}
                autoComplete="one-time-code"
                value={values.secondFactor}
                onChange={(event) => setValues({ ...values, secondFactor: normalizeSecondFactor(event.target.value) })}
                placeholder={factorMode === "totp" ? "000 000" : "XXXX-XXXX"}
                autoFocus
                required
              />
            </Field>
            <button
              className="text-button"
              type="button"
              onClick={() => {
                setFactorMode(factorMode === "totp" ? "recovery" : "totp")
                setValues({ ...values, secondFactor: "" })
                setError("")
              }}
            >
              {factorMode === "totp" ? "Использовать recovery-код" : "Ввести код из приложения"}
            </button>
            <button
              className="text-button"
              type="button"
              onClick={() => {
                setStep("credentials")
                setError("")
                setValues({ ...values, secondFactor: "" })
              }}
            >
              ← Другой аккаунт
            </button>
          </>
        )}
        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}
        <Button size="lg" loading={submitting} type="submit">
          {step === "credentials" ? "Продолжить" : "Подтвердить"}
        </Button>
      </form>
      {registrationEnabled ? (
        <div className="auth-card__footer">
          Впервые здесь? <Link to="/register">Создать семью</Link>
        </div>
      ) : (
        <div className="auth-card__footer auth-card__footer--quiet">Новый аккаунт создаётся по приглашению.</div>
      )}
    </AuthFrame>
  )
}

export function RegisterPage() {
  const { invitationToken } = useParams()
  const { registrationEnabled, ready } = usePublicConfig()
  const { user, booting, register } = useAuth()
  const navigate = useNavigate()
  const [values, setValues] = useState({
    displayName: "",
    familyName: "",
    login: "",
    password: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Moscow"
  })
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)

  if (!booting && user) return <Navigate to="/" replace />
  if (!ready)
    return (
      <AuthFrame quote="Собираем семейное пространство.">
        <LoadingState rows={3} />
      </AuthFrame>
    )
  if (!registrationEnabled && !invitationToken)
    return (
      <AuthFrame quote="Ваша семья начинается с приглашения.">
        <div className="auth-card__heading">
          <h2>Регистрация по приглашению</h2>
          <p>Администратор сервера пришлёт одноразовую ссылку для создания новой семьи.</p>
        </div>
        <div className="info-callout">
          <Icon name="lock" size={19} />
          <p>Открытая регистрация выключена. Это защищает частный семейный сервер от посторонних.</p>
        </div>
        <div className="auth-card__footer">
          Уже есть аккаунт? <Link to="/login">Войти</Link>
        </div>
      </AuthFrame>
    )

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError("")
    try {
      await register({
        ...values,
        login: values.login.trim(),
        deviceName: navigator.userAgent.includes("Mobile") ? "Телефон" : "Браузер",
        ...(invitationToken ? { invitationToken } : {})
      })
      showToast("Семейное пространство создано")
      navigate("/", { replace: true })
    } catch (reason) {
      setError(toApiMessage(reason, "Не удалось создать семью"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthFrame quote="Придумайте правила, которые хочется соблюдать.">
      <div className="auth-card__heading">
        <h2>Создать семью</h2>
        <p>Вы станете владельцем и сможете пригласить близких.</p>
      </div>
      <form className="form-stack form-grid" onSubmit={submit}>
        <Field label="Как вас зовут">
          <input
            required
            maxLength={80}
            autoComplete="name"
            value={values.displayName}
            onChange={(event) => setValues({ ...values, displayName: event.target.value })}
            placeholder="Никита"
            autoFocus
          />
        </Field>
        <Field label="Название семьи">
          <input
            required
            maxLength={100}
            value={values.familyName}
            onChange={(event) => setValues({ ...values, familyName: event.target.value })}
            placeholder="Дом Соколовых"
          />
        </Field>
        <Field label="Логин">
          <input
            required
            minLength={3}
            autoComplete="username"
            value={values.login}
            onChange={(event) => setValues({ ...values, login: event.target.value.trimStart() })}
            placeholder="nikita"
          />
        </Field>
        <Field label="Пароль" hint="Не короче 10 символов">
          <input
            required
            minLength={10}
            type="password"
            autoComplete="new-password"
            value={values.password}
            onChange={(event) => setValues({ ...values, password: event.target.value })}
            placeholder="Придумайте надёжный пароль"
          />
        </Field>
        <Field label="Часовой пояс" hint="Для начислений по расписанию">
          <input
            required
            value={values.timezone}
            onChange={(event) => setValues({ ...values, timezone: event.target.value })}
          />
        </Field>
        {error ? (
          <div className="form-error form-grid__wide" role="alert">
            {error}
          </div>
        ) : null}
        <Button className="form-grid__wide" size="lg" loading={submitting} type="submit">
          Создать семейное пространство
        </Button>
      </form>
      <div className="auth-card__footer">
        Уже есть семья? <Link to="/login">Войти</Link>
      </div>
    </AuthFrame>
  )
}

interface AcceptInviteResponse extends Partial<AuthSession> {
  family: FamilySummary
}

export function invitationTokenFromInput(value: string) {
  const trimmed = value.trim()
  try {
    const url = new URL(trimmed)
    const parts = url.pathname.split("/").filter(Boolean)
    return decodeURIComponent(parts.at(-1) ?? trimmed)
  } catch {
    return trimmed
  }
}

export function InvitePage() {
  const { token: routeToken } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [values, setValues] = useState({
    token: routeToken ?? "",
    displayName: "",
    login: "",
    password: "",
    secondFactor: ""
  })
  const [factorStep, setFactorStep] = useState(false)
  const [factorMode, setFactorMode] = useState<"totp" | "recovery">("totp")
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const lastAutoCode = useRef("")

  const acceptInvitation = useCallback(
    async (secondFactor?: string) => {
      setSubmitting(true)
      setError("")
      try {
        const response = await api.post<AcceptInviteResponse>("/families/invitations/accept", {
          token: invitationTokenFromInput(values.token),
          ...(!user
            ? {
                login: values.login.trim(),
                password: values.password,
                displayName: values.displayName,
                ...(secondFactor ? { secondFactor } : {})
              }
            : {})
        })
        if (response.accessToken && response.user) acceptSession(response as AuthSession)
        else if (!user) await refreshSession()
        localStorage.setItem("family-coin.active-family", response.family.id)
        await queryClient.invalidateQueries({ queryKey: ["families"] })
        showToast(`Вы присоединились к семье «${response.family.name}»`)
        navigate("/", { replace: true })
      } catch (reason) {
        if (!user && secondFactorRequired(reason) && !secondFactor) {
          setFactorStep(true)
          setError("")
        } else {
          setError(toApiMessage(reason, "Не удалось принять приглашение"))
        }
      } finally {
        setSubmitting(false)
      }
    },
    [navigate, queryClient, user, values.displayName, values.login, values.password, values.token]
  )

  useEffect(() => {
    if (!factorStep || factorMode !== "totp" || submitting) return
    const raw = rawSecondFactor(values.secondFactor)
    if (!/^\d{6}$/.test(raw)) {
      lastAutoCode.current = ""
      return
    }
    if (lastAutoCode.current === raw) return
    lastAutoCode.current = raw
    void acceptInvitation(raw)
  }, [acceptInvitation, factorMode, factorStep, submitting, values.secondFactor])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void acceptInvitation(factorStep ? rawSecondFactor(values.secondFactor) : undefined)
  }

  return (
    <AuthFrame quote="Дом начинается с приглашения.">
      <div className="auth-card__heading">
        <h2>{factorStep ? "Подтвердите восстановление" : "Принять приглашение"}</h2>
        <p>
          {factorStep
            ? "Приглашение уже было принято этим аккаунтом. Подтвердите вход вторым фактором."
            : user
              ? "Добавим семью к вашему аккаунту."
              : "Создадим ваш аккаунт и сразу откроем семью."}
        </p>
      </div>
      <form className="form-stack" onSubmit={submit}>
        {!factorStep ? (
          <Field label="Ссылка или код приглашения">
            <input
              required
              minLength={12}
              value={values.token}
              onChange={(event) => setValues({ ...values, token: event.target.value })}
              placeholder="Вставьте ссылку целиком"
              autoFocus={!routeToken}
            />
          </Field>
        ) : null}
        {!user && !factorStep ? (
          <>
            <Field label="Как вас зовут">
              <input
                required
                value={values.displayName}
                onChange={(event) => setValues({ ...values, displayName: event.target.value })}
                placeholder="Имя в семье"
              />
            </Field>
            <Field label="Логин">
              <input
                required
                minLength={3}
                autoComplete="username"
                value={values.login}
                onChange={(event) => setValues({ ...values, login: event.target.value.trimStart() })}
                placeholder="Ваш логин"
              />
            </Field>
            <Field label="Пароль" hint="Не короче 10 символов">
              <input
                required
                minLength={10}
                type="password"
                autoComplete="new-password"
                value={values.password}
                onChange={(event) => setValues({ ...values, password: event.target.value })}
              />
            </Field>
          </>
        ) : null}
        {!user && factorStep ? (
          <>
            <Field
              label={factorMode === "totp" ? "Код 2FA" : "Recovery-код"}
              hint={factorMode === "totp" ? "Отправим автоматически после шести цифр." : "Код используется один раз."}
            >
              <input
                className={factorMode === "totp" ? "otp-input" : ""}
                inputMode={factorMode === "totp" ? "numeric" : "text"}
                autoComplete="one-time-code"
                required
                autoFocus
                value={values.secondFactor}
                onChange={(event) => setValues({ ...values, secondFactor: normalizeSecondFactor(event.target.value) })}
                placeholder={factorMode === "totp" ? "000 000" : "XXXX-XXXX"}
              />
            </Field>
            <button
              className="text-button"
              type="button"
              onClick={() => {
                setFactorMode(factorMode === "totp" ? "recovery" : "totp")
                setValues({ ...values, secondFactor: "" })
                setError("")
              }}
            >
              {factorMode === "totp" ? "Использовать recovery-код" : "Ввести код из приложения"}
            </button>
            <button
              className="text-button"
              type="button"
              onClick={() => {
                setFactorStep(false)
                setValues({ ...values, secondFactor: "" })
                setError("")
              }}
            >
              ← Проверить данные аккаунта
            </button>
          </>
        ) : null}
        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}
        <Button size="lg" loading={submitting} type="submit">
          {factorStep ? "Подтвердить" : "Войти в семью"}
        </Button>
      </form>
      {!user && !factorStep ? (
        <div className="auth-card__footer">
          Уже есть аккаунт?{" "}
          <Link to="/login" state={{ from: `${location.pathname}${location.search}` }}>
            Войти сначала
          </Link>
        </div>
      ) : null}
    </AuthFrame>
  )
}

export function NotFoundPage() {
  return (
    <div className="not-found">
      <span>🧭</span>
      <h1>Здесь ничего нет</h1>
      <p>Похоже, эта семейная тропинка никуда не ведёт.</p>
      <Link className="button button--primary button--md" to="/">
        Вернуться домой
      </Link>
    </div>
  )
}
